import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { useAuth } from './AuthContext'
import { useBlocks } from '../hooks/useBlocks'
import { supabase } from '../lib/supabase'
import { handleAuthError, fetchWithAuthRetry } from '../lib/authErrorHandlers'

const OnlineStatusContext = createContext({ isOnline: () => false })
export const useOnlineStatus = () => useContext(OnlineStatusContext)

export function OnlineStatusProvider({ children }) {
  const { user, getToken } = useAuth()
  const blockedIds = useBlocks()
  const [onlineSet, setOnlineSet] = useState(new Set())
  const channelRef = useRef(null)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (!user) return

    let cancelled = false

    // Fetch privacy setting through the Express API (not direct Supabase —
    // the client-side anon key can't SELECT from profiles due to RLS).
    getToken().then(token => {
      if (cancelled || !token) return
      return fetchWithAuthRetry('/api/profile/', {
        headers: { Authorization: `Bearer ${token}` },
      }).then(async r => {
        if (!r.ok) {
          await handleAuthError(r)
          return null
        }
        return r.json()
      })
    }).then(data => {
      if (cancelled || !data) return

      const showOnline = data.showOnlineStatus !== false

      const channel = supabase.channel('online-users')
      channelRef.current = channel

      channel
        .on('presence', { event: 'sync' }, () => {
          const state = channel.presenceState()
          const ids = new Set()
          Object.values(state).forEach(entries =>
            entries.forEach(e => { if (e.user_id) ids.add(e.user_id) })
          )
          setOnlineSet(ids)
        })
        .subscribe(async (status) => {
          // Always subscribe (so user can see others), but only track own
          // presence if privacy setting allows it.
          if (status === 'SUBSCRIBED' && showOnline) {
            await channel.track({ user_id: user.id })
          }
        })

      // Server heartbeat — always fires regardless of privacy setting so
      // last_seen_at stays fresh for the admin online-users panel. The privacy
      // setting only controls Presence tracking (above) and the public API.
      async function heartbeat() {
        if (document.visibilityState !== 'visible') return
        const token = await getToken()
        if (token) {
          fetchWithAuthRetry('/api/profile/heartbeat', {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}` },
          }).then(handleAuthError).catch(() => {})
        }
      }
      heartbeat()
      intervalRef.current = setInterval(heartbeat, 60_000)
    }).catch(err => {
      if (import.meta.env.DEV) {
        console.warn('OnlineStatusContext: privacy preload failed', err)
      }
    })

    return () => {
      cancelled = true
      clearInterval(intervalRef.current)
      if (channelRef.current) {
        channelRef.current.untrack()
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const isOnline = (userId) => onlineSet.has(userId) && !blockedIds.has(userId)

  return (
    <OnlineStatusContext.Provider value={{ isOnline }}>
      {children}
    </OnlineStatusContext.Provider>
  )
}
